import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api/client";
import { serverErrorCode, serverErrorMessage, serverRawMessage } from "@/lib/api/error-message";
import type {
  GalleryArchiveJob,
  GalleryAssetPage,
  GalleryCompletedPart,
  GallerySignedDownload,
  GallerySignedPart,
  GallerySignedVersionURLs,
  GalleryUploadBatch,
  GalleryUploadBatchInput,
  GalleryUploadSession,
  GalleryAlbumTree,
  GalleryQuickFilters,
  GalleryAlbum,
  GalleryAlbumMergeResult,
  GalleryAlbumItemsMoveResult,
  GalleryAsset,
  GalleryTrash,
  GalleryAssetVersion,
  GalleryComment,
  GalleryCreatedShare,
  GallerySelectionState,
  GalleryStaffResubmissionList,
  GalleryOriginalsRetention,
  GalleryShare,
  GalleryTag,
  GalleryAlbumTag,
  GalleryAlbumReturn,
  GalleryAlbumReturnInput,
  PublicGallery,
  PublicGalleryAssetPage,
  PublicGalleryArchiveJob,
  PublicGalleryComment,
  PublicGallerySelectionState,
} from "./types";
import type { components } from "@/lib/api/generated/schema";

export type GalleryReturnReason = components["schemas"]["GalleryReturnReason"];
type GalleryAlbumInput = components["schemas"]["GalleryAlbumInput"];
type GalleryAlbumPatch = components["schemas"]["GalleryAlbumPatch"];
type GalleryAlbumItemInput = components["schemas"]["GalleryAlbumItemInput"];
type GalleryMarkerInput = components["schemas"]["GalleryMarkerInput"];
type GalleryCommentInput = components["schemas"]["GalleryCommentInput"];
type GalleryShareInput = components["schemas"]["GalleryShareInput"];

export type PublicGalleryScope =
  | { kind: "share"; token: string }
  | { kind: "portal"; orderId: string };

const galleryPublicAPIBase = import.meta.env.VITE_API_URL ?? "http://localhost:8080/api/v1";

export const galleryKeys = {
  all: ["gallery"] as const,
  root: (orderId: string) => ["gallery", "order", orderId] as const,
  // Nằm DƯỚI khoá cây của đơn: mọi chỗ đang làm mới `["gallery", "order"]`
  // (xoá album, chốt / mở lại lượt chọn) tự làm mới luôn bộ lọc nhanh.
  quickFilters: (orderId: string) => ["gallery", "order", orderId, "quick-filters"] as const,
  assets: (albumId: string, descendants: boolean) =>
    ["gallery", "assets", albumId, descendants] as const,
  comments: (assetId: string) => ["gallery", "comments", assetId] as const,
  archive: (jobId: string) => ["gallery", "archive", jobId] as const,
  selection: (albumId: string) => ["gallery", "selection", albumId] as const,
  // Dưới khoá cây của đơn: làm mới cây (chốt, mở lại…) là làm mới luôn khối này.
  resubmissions: (orderId: string) => ["gallery", "order", orderId, "resubmissions"] as const,
  originals: (orderId: string) => ["gallery", "order", orderId, "originals"] as const,
  trash: (orderId: string) => ["gallery", "trash", orderId] as const,
  returnReasons: () => ["gallery", "return-reasons"] as const,
};

export const GALLERY_FORBIDDEN_MESSAGE =
  "Bạn chưa được mở album của đơn này — nhờ quản lý thêm bạn vào ê-kíp của đơn.";

/** Error from a staff Gallery call, keeping the backend error code. */
export class GalleryApiError extends Error {
  readonly code: string | undefined;
  /** Số giây máy chủ bảo chờ (tiêu đề Retry-After) khi từ chối vì gọi quá nhanh. */
  readonly retryAfterSeconds: number | undefined;
  constructor(message: string, code?: string, retryAfterSeconds?: number) {
    super(message);
    this.name = "GalleryApiError";
    this.code = code;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/** Máy chủ từ chối vì người này gọi quá nhanh (429) — chờ rồi gọi lại là qua. */
export function isGalleryRateLimited(error: unknown): error is GalleryApiError {
  return error instanceof GalleryApiError && error.code === "rate_limited";
}

/**
 * Lỗi của một lượt gọi trong luồng tải ảnh. 429 luôn mang mã "rate_limited" và
 * số giây chờ — kể cả máy chủ cũ chỉ trả thân phẳng, mã không đọc ra được.
 */
function uploadCallError(error: unknown, response: Response, fallback: string): Error {
  if (response.status !== 429) return galleryError(error, fallback);
  const seconds = Number(response.headers.get("Retry-After") ?? "");
  return new GalleryApiError(
    serverErrorMessage({ error: { code: "rate_limited" } }, fallback),
    "rate_limited",
    Number.isFinite(seconds) && seconds > 0 ? seconds : undefined,
  );
}

export function isGalleryForbidden(error: unknown): boolean {
  return error instanceof GalleryApiError && error.code === "forbidden";
}

/** Album mang nhãn "Tài liệu mật" (QĐ 0305) mà người xem không thuộc nhóm được mở. */
export function isGalleryConfidential(error: unknown): boolean {
  return error instanceof GalleryApiError && error.code === "album_confidential";
}

/** Đơn chưa có album (GET cây album trả 404). */
export function isGalleryNotFound(error: unknown): boolean {
  return error instanceof GalleryApiError && error.code === "not_found";
}

function galleryError(error: unknown, fallback: string): Error {
  const code = serverErrorCode(error) || undefined;
  if (code === "forbidden") {
    // The backend's English forbidden text is technical; staff need to know
    // who to ask instead. A refusal written in Vietnamese names the exact rule
    // (the retouch team's branch fence) and reaches staff who ARE on the team,
    // so it is shown as sent: serverErrorMessage returns a message untouched
    // only when it is Vietnamese.
    const shown = serverErrorMessage(error, "");
    if (!shown || shown !== serverRawMessage(error)) {
      return new GalleryApiError(GALLERY_FORBIDDEN_MESSAGE, code);
    }
    return new GalleryApiError(shown, code);
  }
  return new GalleryApiError(serverErrorMessage(error, fallback), code);
}

function requireData<T>(data: T | undefined, error: unknown, fallback: string): T {
  if (error || data === undefined) throw galleryError(error, fallback);
  return data;
}

/**
 * Kho chứa đứng sau một cây album: "order" — album của đơn (cửa
 * `/orders/{id}/gallery…`); "loose" — album ngoài đơn (QĐ 0306, cửa
 * `/gallery/loose-albums/{id}…`). Hai loại dùng chung mọi cửa theo mã thư mục /
 * mã ảnh; chỉ các cửa theo KHO CHỨA (cây, thùng rác) là khác đường.
 */
export type GallerySource = "order" | "loose";

export async function getGalleryTree(orderId: string, source: GallerySource = "order"): Promise<GalleryAlbumTree> {
  if (source === "loose") {
    const { data, error } = await api.GET("/gallery/loose-albums/{id}", {
      params: { path: { id: orderId } },
    });
    return requireData(data, error, "Không tải được album").tree;
  }
  const { data, error } = await api.GET("/orders/{id}/gallery", {
    params: { path: { id: orderId } },
  });
  return requireData(data, error, "Không tải được thư viện ảnh");
}

/**
 * Tạo album cho đơn cần album mà chưa có (CEO chốt 1a, 02/10/2026) rồi trả cây
 * album. Gọi lặp vô hại. Đơn không dùng album thì máy chủ từ chối kèm lý do.
 */
export async function ensureGalleryForUpload(orderId: string): Promise<GalleryAlbumTree> {
  const { data, error } = await api.POST("/orders/{id}/gallery", {
    params: { path: { id: orderId } },
  });
  return requireData(data, error, "Không tạo được album cho đơn");
}

/** Ba bộ lọc nhanh cột trái Kho ảnh — xem backend album_counts.go. */
export async function getGalleryQuickFilters(orderId: string): Promise<GalleryQuickFilters> {
  const { data, error } = await api.GET("/orders/{id}/gallery/quick-filters", {
    params: { path: { id: orderId } },
  });
  return requireData(data, error, "Không tải được bộ lọc nhanh của album");
}

export async function getAlbumAssets(args: {
  albumId: string;
  includeDescendants?: boolean;
  cursor?: string;
  limit?: number;
}): Promise<GalleryAssetPage> {
  const { data, error } = await api.GET("/gallery/albums/{albumId}/assets", {
    params: {
      path: { albumId: args.albumId },
      query: {
        includeDescendants: args.includeDescendants ?? false,
        ...(args.cursor ? { cursor: args.cursor } : {}),
        ...(args.limit ? { limit: args.limit } : {}),
      },
    },
  });
  return requireData(data, error, "Không tải được ảnh trong thư mục");
}

/** Mã lỗi riêng của màn khi lượt giữ chỗ vượt `maxBatchFiles` — hộp tải chia nhỏ rồi gửi lại. */
export const UPLOAD_BATCH_LIMIT_CODE = "upload_batch_limit";
/**
 * Mã lỗi riêng của màn khi lô gửi lại mã tải của một lượt máy chủ đã đóng — lô
 * giữ chỗ trước của chính các tệp đó hỏng giữa chừng. Hộp tải đổi mã mới rồi gửi lại.
 */
export const UPLOAD_CLOSED_CODE = "upload_closed";

export async function initUploadBatch(
  albumId: string,
  body: GalleryUploadBatchInput,
): Promise<GalleryUploadBatch> {
  const { data, error, response } = await api.POST("/gallery/albums/{albumId}/upload-batches", {
    params: { path: { albumId } },
    body,
  });
  if (error && /maxBatchFiles/.test(serverRawMessage(error))) {
    throw new GalleryApiError(
      serverErrorMessage(error, "Chọn quá nhiều ảnh cho một lần tải"),
      UPLOAD_BATCH_LIMIT_CODE,
    );
  }
  if (error && /clientUploadId belongs to a closed upload/.test(serverRawMessage(error))) {
    throw new GalleryApiError(
      serverErrorMessage(error, "Lượt tải trước của các ảnh này đã hỏng giữa chừng — chọn lại ảnh để tải"),
      UPLOAD_CLOSED_CODE,
    );
  }
  if (error || data === undefined) throw uploadCallError(error, response, "Không khởi tạo được lượt tải ảnh");
  return data;
}

export async function getUploadSession(uploadId: string): Promise<GalleryUploadSession> {
  const { data, error } = await api.GET("/gallery/uploads/{uploadId}", {
    params: { path: { uploadId } },
  });
  return requireData(data, error, "Không đọc được trạng thái tải ảnh");
}

/** Số lượt tải tối đa máy chủ nhận trong một lần hỏi tiến độ (`maxUploadStatusBatch`). */
export const UPLOAD_STATUS_BATCH = 200;

/**
 * Tiến độ của nhiều lượt tải trong MỘT lượt gọi. Lượt người hỏi không được xem
 * thì máy chủ bỏ khỏi kết quả, không làm hỏng cả câu trả lời.
 */
export async function getUploadStatuses(uploadIds: string[]): Promise<GalleryUploadSession[]> {
  const { data, error, response } = await api.POST("/gallery/upload-statuses", {
    body: { uploadIds },
  });
  if (error || data === undefined) throw uploadCallError(error, response, "Không đọc được tiến độ xử lý ảnh");
  return data.items;
}

export async function presignUploadParts(
  uploadId: string,
  partNumbers: number[],
): Promise<GallerySignedPart[]> {
  const { data, error, response } = await api.POST("/gallery/uploads/{uploadId}/parts/presign", {
    params: { path: { uploadId } },
    body: { partNumbers },
  });
  if (error || data === undefined) throw uploadCallError(error, response, "Không ký được phần tải ảnh");
  return data.parts;
}

/**
 * Đang gửi mà không nhích byte nào chừng này = đường truyền chết (4G/5G đổi trạm,
 * NAT rớt…). Trước 24/10/2026 không có hạn: phần đó treo mãi, cả lô đứng ở
 * "Đang tải" không báo gì (đo: 8 MB đã gửi, kho im — sau 90 giây vẫn treo).
 */
export const PART_SEND_STALL_MS = 60_000;
/**
 * Gửi hết thân phần rồi mà chưa có trả lời: Cloudflare tự trả 524 sau 100 giây
 * chờ máy kho, nên chờ quá chừng này là treo thật.
 */
export const PART_RESPONSE_WAIT_MS = 120_000;

/** Lỗi gửi một phần ảnh lên kho. `status` 0 = không tới được kho (mất mạng, treo, bị chặn). */
export class PartUploadError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = "PartUploadError";
    this.status = status;
  }
}

export async function uploadPresignedPart(
  url: string,
  body: Blob,
  signal?: AbortSignal,
  onProgress?: (uploadedBytes: number) => void,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    let timer: ReturnType<typeof setTimeout> | undefined;
    let settled = false;
    const finish = (settle: () => void) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      signal?.removeEventListener("abort", abort);
      settle();
    };
    // Mỗi lần nhích byte là đặt lại hạn; hết hạn thì cắt lượt gửi và báo lỗi
    // mạng — putWithRetry gửi lại đúng phần đó.
    const arm = (ms: number) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        finish(() => reject(new PartUploadError("Mạng đứng quá lâu khi gửi ảnh lên kho lưu trữ", 0)));
        request.abort();
      }, ms);
    };
    function abort() {
      finish(() => reject(new DOMException("Đã tạm dừng tải ảnh", "AbortError")));
      request.abort();
    }

    request.open("PUT", url);
    request.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress?.(event.loaded);
      const sent = event.lengthComputable && event.loaded >= event.total;
      arm(sent ? PART_RESPONSE_WAIT_MS : PART_SEND_STALL_MS);
    };
    request.upload.onload = () => arm(PART_RESPONSE_WAIT_MS);
    request.onload = () => {
      finish(() => {
        if (request.status < 200 || request.status >= 300) {
          reject(new PartUploadError("Không tải được phần ảnh lên kho lưu trữ", request.status));
          return;
        }
        const eTag = request.getResponseHeader("ETag")?.trim();
        if (!eTag) {
          reject(new PartUploadError("Kho lưu trữ không trả về ETag", request.status));
          return;
        }
        resolve(eTag);
      });
    };
    request.onerror = () =>
      finish(() => reject(new PartUploadError("Không tải được phần ảnh lên kho lưu trữ", 0)));
    request.onabort = () => finish(() => reject(new DOMException("Đã tạm dừng tải ảnh", "AbortError")));
    signal?.addEventListener("abort", abort, { once: true });
    arm(PART_SEND_STALL_MS);
    request.send(body);
  });
}

export async function completeUpload(uploadId: string, parts: GalleryCompletedPart[]) {
  const { data, error, response } = await api.POST("/gallery/uploads/{uploadId}/complete", {
    params: { path: { uploadId } },
    body: { parts },
  });
  if (error || data === undefined) throw uploadCallError(error, response, "Không hoàn tất được lượt tải ảnh");
  return data;
}

export async function abortUpload(uploadId: string): Promise<void> {
  const { error } = await api.DELETE("/gallery/uploads/{uploadId}", {
    params: { path: { uploadId } },
  });
  if (error) throw galleryError(error, "Không huỷ được lượt tải ảnh");
}

export async function getVersionURLs(
  assetId: string,
  versionId: string,
): Promise<GallerySignedVersionURLs> {
  const { data, error } = await api.GET("/gallery/assets/{assetId}/versions/{versionId}/urls", {
    params: { path: { assetId, versionId } },
  });
  return requireData(data, error, "Không mở được ảnh");
}

export async function createGalleryAlbum(
  parentId: string,
  body: GalleryAlbumInput,
): Promise<GalleryAlbum> {
  const { data, error } = await api.POST("/gallery/albums/{albumId}/children", {
    params: { path: { albumId: parentId } },
    body,
  });
  return requireData(data, error, "Không tạo được thư mục");
}

export async function updateGalleryAlbum(
  albumId: string,
  body: GalleryAlbumPatch,
): Promise<GalleryAlbum> {
  const { data, error } = await api.PATCH("/gallery/albums/{albumId}", {
    params: { path: { albumId } },
    body,
  });
  return requireData(data, error, "Không cập nhật được thư mục");
}

/**
 * Trả album về phòng ảnh chụp lại, kèm hoặc KHÔNG kèm phiếu lỗi.
 *
 * Máy chủ ghi phiếu lỗi TRONG CÙNG giao dịch với lượt trả: trả hỏng thì phiếu
 * cũng không tồn tại. Nên chỗ này không được tự ghi phiếu bằng một lượt gọi
 * thứ hai — làm thế là có ngày đổ oan một cú không lý do.
 */
/**
 * Ba lý do bày thành thẻ trên hộp trả album.
 *
 * KHÔNG phải danh mục mã lỗi (`/incidents/penalty-catalog`): cửa kia bày cả
 * nhóm nghề và lọc theo nghề người gọi, nên nhân viên hậu kỳ xin nhóm ảnh nhận
 * về mảng rỗng. Cửa này bày đúng ba lý do của MỘT việc, cho đúng người bấm được
 * nút trả — không nới ranh giới thông tin, cũng không để hộp trống.
 */
export async function listGalleryReturnReasons(): Promise<GalleryReturnReason[]> {
  const { data, error } = await api.GET("/gallery/return-reasons", {});
  return requireData(data, error, "Không đọc được danh sách lý do trả album").items;
}

export async function returnGalleryAlbumToPhoto(
  albumId: string,
  body: GalleryAlbumReturnInput,
): Promise<GalleryAlbumReturn> {
  const { data, error } = await api.POST("/gallery/albums/{albumId}/return-to-photo", {
    params: { path: { albumId } },
    body,
  });
  return requireData(data, error, "Không trả được album về phòng ảnh");
}

export async function deleteGalleryAlbum(
  albumId: string,
  itemDisposition: "move_to_parent" | "unlink",
): Promise<void> {
  const { error } = await api.DELETE("/gallery/albums/{albumId}", {
    params: { path: { albumId } },
    body: { itemDisposition },
  });
  if (error) throw galleryError(error, "Không xoá được thư mục");
}

/**
 * Gộp album vào album cha — MỘT lượt gọi, máy chủ làm trọn trong một giao dịch
 * (QĐ 0225). `expectedRevision` là revision trên cây người bấm đang thấy: cây
 * đã đổi dưới tay người khác thì máy chủ trả 409 thay vì gộp mò.
 */
export async function mergeGalleryAlbum(
  albumId: string,
  expectedRevision: number,
): Promise<GalleryAlbumMergeResult> {
  const { data, error } = await api.POST("/gallery/albums/{albumId}/merge-into-parent", {
    params: { path: { albumId } },
    body: { expectedRevision },
  });
  return requireData(data, error, "Không gộp được thư mục");
}

/**
 * Chuyển cả lô tấm của `fromAlbumId` VÀO thư mục `targetAlbumId` trong MỘT
 * giao dịch (CEO chốt 1a · 2a, 04/10/2026): hoặc chuyển hết, hoặc không tấm
 * nào đổi chỗ.
 */
export async function moveGalleryAlbumItems(
  targetAlbumId: string,
  fromAlbumId: string,
  assetIds: string[],
): Promise<GalleryAlbumItemsMoveResult> {
  const { data, error } = await api.POST("/gallery/albums/{albumId}/items/move", {
    params: { path: { albumId: targetAlbumId } },
    body: { fromAlbumId, assetIds },
  });
  return requireData(data, error, "Không chuyển được ảnh sang thư mục khác");
}

/**
 * Thùng rác của NGƯỜI ĐANG XEM trong đơn (QĐ 0225, CEO chốt 1b): chỉ tấm chính
 * họ đã xoá — "rác của ai người đó xem" (CEO chốt 27/09/2026).
 */
export async function getGalleryTrash(orderId: string, source: GallerySource = "order"): Promise<GalleryTrash> {
  const { data, error } =
    source === "loose"
      ? await api.GET("/gallery/loose-albums/{id}/trash", { params: { path: { id: orderId } } })
      : await api.GET("/orders/{id}/gallery/trash", { params: { path: { id: orderId } } });
  return requireData(data, error, "Không tải được thùng rác của album");
}

/** Khôi phục nhiều tấm trong MỘT giao dịch; tấm đã ra khỏi thùng rác thì bỏ qua. */
export async function restoreGalleryTrash(
  orderId: string,
  assetIds: string[],
  source: GallerySource = "order",
): Promise<{ restoredCount: number }> {
  const init = { params: { path: { id: orderId } }, body: { assetIds } };
  const { data, error } =
    source === "loose"
      ? await api.POST("/gallery/loose-albums/{id}/trash/restore", init)
      : await api.POST("/orders/{id}/gallery/trash/restore", init);
  return requireData(data, error, "Không khôi phục được ảnh");
}

/** "Xoá hẳn" tấm trong thùng rác của chính mình; tấm đã xoá hẳn thì bỏ qua. */
export async function purgeGalleryTrash(
  orderId: string,
  assetIds: string[],
  source: GallerySource = "order",
): Promise<{ purgedCount: number }> {
  const init = { params: { path: { id: orderId } }, body: { assetIds } };
  const { data, error } =
    source === "loose"
      ? await api.POST("/gallery/loose-albums/{id}/trash/purge", init)
      : await api.POST("/orders/{id}/gallery/trash/purge", init);
  return requireData(data, error, "Không xoá hẳn được ảnh");
}

export async function updateGalleryAlbumItem(
  albumId: string,
  assetId: string,
  body: GalleryAlbumItemInput,
) {
  const { data, error } = await api.PUT("/gallery/albums/{albumId}/items/{assetId}", {
    params: { path: { albumId, assetId } },
    body,
  });
  return requireData(data, error, "Không cập nhật được ảnh trong thư mục");
}

export async function removeGalleryAlbumItem(albumId: string, assetId: string): Promise<void> {
  const { error } = await api.DELETE("/gallery/albums/{albumId}/items/{assetId}", {
    params: { path: { albumId, assetId } },
  });
  if (error) throw galleryError(error, "Không gỡ được ảnh khỏi thư mục");
}

/**
 * "Xoá N ảnh" (`assetIds`) hoặc "Xoá tất cả ảnh trong thư mục" (`all`): gỡ khỏi
 * thư mục trong MỘT giao dịch. Tấm không còn ở thư mục nào khác vào thùng rác
 * của người bấm; `all` chỉ gỡ tấm nằm thẳng trong thư mục, thư mục con giữ ảnh.
 */
export async function unlinkGalleryAlbumItems(
  albumId: string,
  body: { assetIds: string[] } | { all: true },
): Promise<{ removedCount: number; trashedCount: number }> {
  const { data, error } = await api.POST("/gallery/albums/{albumId}/items/unlink", {
    params: { path: { albumId } },
    body,
  });
  return requireData(data, error, "Không xoá được ảnh");
}

export async function updateGalleryMarkers(
  assetId: string,
  body: GalleryMarkerInput,
): Promise<GalleryAsset> {
  const { data, error } = await api.PATCH("/gallery/assets/{assetId}/markers", {
    params: { path: { assetId } },
    body,
  });
  return requireData(data, error, "Không cập nhật được cờ ảnh");
}

export async function updateGallerySelection(
  albumId: string,
  assetId: string,
  selected: boolean,
): Promise<GallerySelectionState> {
  const { data, error } = await api.PUT("/gallery/albums/{albumId}/selections/{assetId}", {
    params: { path: { albumId, assetId } },
    body: { selected },
  });
  return requireData(data, error, "Không cập nhật được ảnh chọn");
}

/**
 * Vòng 2 — "ảnh chọn cuối".
 *
 * Cửa máy chủ có từ lượt mở đường nhân viên chốt hộ khách (gallery/handler.go),
 * nhưng màn hình chưa bao giờ gọi: nút "Chọn cuối" nằm sẵn trong AssetInspector,
 * đọc đúng `finalSelected` để đổi màu, mà bị `disabled` cứng và không có
 * onClick. Nhân viên tick được vòng 1 rồi KẸT.
 */
export async function updateGalleryFinalSelection(
  albumId: string,
  assetId: string,
  selected: boolean,
): Promise<GallerySelectionState> {
  const { data, error } = await api.PUT("/gallery/albums/{albumId}/final-selections/{assetId}", {
    params: { path: { albumId, assetId } },
    body: { selected },
  });
  return requireData(data, error, "Không cập nhật được ảnh khách chốt");
}

export async function getGallerySelection(albumId: string): Promise<GallerySelectionState> {
  const { data, error } = await api.GET("/gallery/albums/{albumId}/selections", {
    params: { path: { albumId } },
  });
  return requireData(data, error, "Không tải được trạng thái chọn ảnh");
}

export async function commitGallerySelection(albumId: string): Promise<GallerySelectionState> {
  const { data, error } = await api.POST("/gallery/albums/{albumId}/selections/commit", {
    params: { path: { albumId } },
  });
  return requireData(data, error, "Không chốt được danh sách ảnh");
}

export async function reopenGallerySelection(
  albumId: string,
  reason: string,
): Promise<GallerySelectionState> {
  const { data, error } = await api.POST("/gallery/albums/{albumId}/selections/reopen", {
    params: { path: { albumId } },
    body: { reason },
  });
  return requireData(data, error, "Không mở lại được danh sách ảnh");
}

/** CSKH bỏ ảnh vượt gói hộ khách (QĐ 0466): danh sách vẫn khoá chốt. */
export async function dropGalleryExtraSelections(
  albumId: string,
  assetIds: string[],
): Promise<GallerySelectionState> {
  const { data, error } = await api.POST("/gallery/albums/{albumId}/selections/drop-extra", {
    params: { path: { albumId } },
    body: { assetIds },
  });
  return requireData(data, error, "Không bỏ được ảnh vượt gói");
}

/** Khối "Ảnh khách gửi lại" (QĐ 0457): tệp khách gửi lại ảnh gốc hết hạn của đơn. */
export async function getStaffResubmissions(albumId: string): Promise<GalleryStaffResubmissionList> {
  const { data, error } = await api.GET("/gallery/albums/{albumId}/original-resubmissions", {
    params: { path: { albumId } },
  });
  return requireData(data, error, "Không tải được ảnh khách gửi lại");
}

export function useStaffResubmissions(orderId: string, albumId: string, enabled = true) {
  return useQuery({
    queryKey: galleryKeys.resubmissions(orderId),
    queryFn: () => getStaffResubmissions(albumId),
    enabled: enabled && albumId.length > 0,
    staleTime: 60_000,
  });
}

export function useAssignResubmission(orderId: string, albumId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, assetId }: { id: string; assetId: string }) => {
      const { data, error } = await api.POST(
        "/gallery/albums/{albumId}/original-resubmissions/{resubmissionId}/assign",
        { params: { path: { albumId, resubmissionId: id } }, body: { assetId } },
      );
      return requireData(data, error, "Chưa ghép được ảnh này");
    },
    onSettled: () => qc.invalidateQueries({ queryKey: galleryKeys.resubmissions(orderId) }),
  });
}

export function useDiscardResubmission(orderId: string, albumId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error, response } = await api.POST(
        "/gallery/albums/{albumId}/original-resubmissions/{resubmissionId}/discard",
        { params: { path: { albumId, resubmissionId: id } } },
      );
      if (error || !response.ok) throw galleryError(error, "Chưa bỏ được tệp này");
    },
    onSettled: () => qc.invalidateQueries({ queryKey: galleryKeys.resubmissions(orderId) }),
  });
}

/** Khối "Ảnh gốc trên Cổng" (QĐ 0459): hạn 15 ngày của ảnh gốc + các lần mở lại. */
export function useOriginalsRetention(orderId: string) {
  return useQuery({
    queryKey: galleryKeys.originals(orderId),
    queryFn: async (): Promise<GalleryOriginalsRetention> => {
      const { data, error } = await api.GET("/orders/{id}/gallery/originals", {
        params: { path: { id: orderId } },
      });
      return requireData(data, error, "Không tải được hạn ảnh gốc");
    },
    enabled: orderId.length > 0,
    staleTime: 60_000,
  });
}

/** Mở lại ảnh gốc đã hết hạn cho khách thêm 7 ngày (QĐ 0459). */
export function useReopenOriginals(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<GalleryOriginalsRetention> => {
      const { data, error } = await api.POST("/orders/{id}/gallery/originals/reopen", {
        params: { path: { id: orderId } },
      });
      return requireData(data, error, "Chưa mở lại được ảnh gốc");
    },
    onSuccess: (data) => qc.setQueryData(galleryKeys.originals(orderId), data),
    onSettled: () => qc.invalidateQueries({ queryKey: galleryKeys.originals(orderId) }),
  });
}

export async function getGalleryComments(assetId: string): Promise<GalleryComment[]> {
  const { data, error } = await api.GET("/gallery/assets/{assetId}/comments", {
    params: { path: { assetId } },
  });
  return requireData(data, error, "Không tải được bình luận").items;
}

export async function createGalleryComment(
  assetId: string,
  body: GalleryCommentInput,
): Promise<GalleryComment> {
  const { data, error } = await api.POST("/gallery/assets/{assetId}/comments", {
    params: { path: { assetId } },
    body,
  });
  return requireData(data, error, "Không gửi được bình luận");
}

export async function updateGalleryComment(
  commentId: string,
  action: "resolve" | "reopen",
): Promise<GalleryComment> {
  const { data, error } = await api.PATCH("/gallery/comments/{commentId}", {
    params: { path: { commentId } },
    body: { action },
  });
  return requireData(data, error, "Không cập nhật được bình luận");
}

export async function getGalleryShares(albumId: string): Promise<GalleryShare[]> {
  const { data, error } = await api.GET("/gallery/albums/{albumId}/shares", {
    params: { path: { albumId } },
  });
  return requireData(data, error, "Không tải được liên kết chia sẻ");
}

export async function createGalleryShare(
  albumId: string,
  body: GalleryShareInput,
): Promise<GalleryCreatedShare> {
  const { data, error } = await api.POST("/gallery/albums/{albumId}/shares", {
    params: { path: { albumId } },
    body,
  });
  return requireData(data, error, "Không tạo được liên kết chia sẻ");
}

export async function revokeGalleryShare(shareId: string): Promise<GalleryShare> {
  const { data, error } = await api.DELETE("/gallery/shares/{shareId}", {
    params: { path: { shareId } },
  });
  return requireData(data, error, "Không thu hồi được liên kết chia sẻ");
}

export async function retryGalleryVersion(
  assetId: string,
  versionId: string,
): Promise<GalleryAssetVersion> {
  const { data, error } = await api.POST(
    "/gallery/assets/{assetId}/versions/{versionId}/retry-processing",
    { params: { path: { assetId, versionId } } },
  );
  return requireData(data, error, "Không thử xử lý lại được ảnh");
}

export async function getGalleryTagCatalog(): Promise<GalleryTag[]> {
  const { data, error } = await api.GET("/gallery/tags");
  return requireData(data, error, "Không tải được danh mục tag").items;
}

export async function getGalleryAlbumTags(albumId: string): Promise<GalleryTag[]> {
  const { data, error } = await api.GET("/gallery/albums/{albumId}/tags", {
    params: { path: { albumId } },
  });
  return requireData(data, error, "Không tải được nhãn của thư mục").items;
}

export async function setGalleryAlbumTag(
  albumId: string,
  tagId: string,
  included: boolean,
  reason?: string,
): Promise<GalleryAlbumTag> {
  if (included) {
    // Lý do chỉ bắt buộc với nhãn "Tài liệu mật" (QĐ 0305); nhãn khác không gửi thân.
    const { data, error } = await api.PUT("/gallery/albums/{albumId}/tags/{tagId}", {
      params: { path: { albumId, tagId } },
      ...(reason ? { body: { reason } } : {}),
    });
    return requireData(data, error, "Không gắn được nhãn thư mục");
  }
  const { data, error } = await api.DELETE("/gallery/albums/{albumId}/tags/{tagId}", {
    params: { path: { albumId, tagId } },
  });
  return requireData(data, error, "Không gỡ được nhãn thư mục");
}

/**
 * Xin một tệp ZIP. `assetIds` thu hẹp về đúng những tấm đó (Lọc nhanh, ô Tìm tên
 * file, ảnh đã tích); `onePerAsset` gói mỗi tấm một tệp, không thư mục con;
 * `originals` lấy bản GỐC của từng tấm, bỏ qua luật bản của thư mục.
 */
export async function requestArchive(args: {
  albumId: string;
  includeDescendants: boolean;
  assetIds?: string[];
  onePerAsset?: boolean;
  originals?: boolean;
}): Promise<GalleryArchiveJob> {
  // Máy chủ đọc `assetIds` vắng hay rỗng là "cả thư mục": gửi tập rỗng đi là
  // xin "không tấm nào" mà nhận cả thư mục. Chặn ngay ở đây.
  if (args.assetIds && args.assetIds.length === 0) {
    throw new Error("Chưa có ảnh nào để tải");
  }
  const { data, error } = await api.POST("/gallery/albums/{albumId}/archives", {
    params: { path: { albumId: args.albumId } },
    body: {
      includeDescendants: args.includeDescendants,
      ...(args.assetIds ? { assetIds: args.assetIds } : {}),
      ...(args.onePerAsset ? { onePerAsset: true } : {}),
      ...(args.originals ? { originals: true } : {}),
    },
  });
  return requireData(data, error, "Không tạo được tệp tải ảnh");
}

export async function getArchive(archiveId: string): Promise<GalleryArchiveJob> {
  const { data, error } = await api.GET("/gallery/archives/{archiveId}", {
    params: { path: { archiveId } },
  });
  return requireData(data, error, "Không đọc được tiến độ đóng gói");
}

export async function getArchiveDownload(archiveId: string): Promise<GallerySignedDownload> {
  const { data, error } = await api.GET("/gallery/archives/{archiveId}/download", {
    params: { path: { archiveId } },
  });
  return requireData(data, error, "Không tạo được liên kết tải ảnh");
}

/**
 * Lý do trả album. Chỉ gọi khi hộp ĐANG MỞ (`enabled`) — danh sách này chỉ có
 * nghĩa bên trong hộp, xin sẵn từ lúc mở trang là một lượt đi mạng cho mọi
 * người xem kho ảnh, kể cả người chẳng bao giờ bấm nút trả.
 *
 * Không đặt `staleTime` dài: danh mục mã lỗi do quản lý sửa được, và một thẻ
 * cũ nằm lại trong bộ nhớ đệm là mời người ta chọn mã mà máy chủ vừa tắt.
 */
export function useGalleryReturnReasons(enabled: boolean) {
  return useQuery({
    queryKey: galleryKeys.returnReasons(),
    queryFn: listGalleryReturnReasons,
    enabled,
    staleTime: 60_000,
  });
}

export function useGalleryTree(orderId: string, enabled = true, source: GallerySource = "order") {
  return useQuery({
    // Cùng khoá cho hai nguồn: một mã kho chứa chỉ thuộc đúng một nguồn, và mọi
    // lượt ghi làm mới `["gallery", "order"]` — cây album ngoài đơn cũng tươi theo.
    queryKey: galleryKeys.root(orderId),
    queryFn: () => getGalleryTree(orderId, source),
    enabled: enabled && orderId.length > 0,
    staleTime: 15_000,
    // A permission refusal will not change on retry; show it right away. Nor
    // will "no album yet" — the order page needs that answer to pick its door.
    retry: (failureCount, error) =>
      !isGalleryForbidden(error) && !isGalleryNotFound(error) && !isGalleryConfidential(error) && failureCount < 3,
  });
}

export function useGalleryQuickFilters(orderId: string, enabled = true) {
  return useQuery({
    queryKey: galleryKeys.quickFilters(orderId),
    queryFn: () => getGalleryQuickFilters(orderId),
    enabled: enabled && orderId.length > 0,
    staleTime: 15_000,
    retry: (failureCount, error) => !isGalleryForbidden(error) && failureCount < 3,
  });
}

export function useGalleryAssets(albumId: string, includeDescendants: boolean, enabled = true) {
  return useQuery({
    queryKey: galleryKeys.assets(albumId, includeDescendants),
    queryFn: () => getAlbumAssets({ albumId, includeDescendants }),
    enabled: enabled && albumId.length > 0,
    staleTime: 10 * 60_000,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: false,
  });
}

export function useInfiniteGalleryAssets(
  albumId: string,
  includeDescendants: boolean,
  enabled = true,
) {
  return useInfiniteQuery({
    queryKey: galleryKeys.assets(albumId, includeDescendants),
    queryFn: ({ pageParam }) =>
      getAlbumAssets({
        albumId,
        includeDescendants,
        ...(pageParam ? { cursor: pageParam } : {}),
        limit: 200,
      }),
    initialPageParam: "",
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: enabled && albumId.length > 0,
    staleTime: 10 * 60_000,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: false,
  });
}

export function useGalleryArchive(archiveId: string, enabled = true) {
  return useQuery({
    queryKey: galleryKeys.archive(archiveId),
    queryFn: () => getArchive(archiveId),
    enabled: enabled && archiveId.length > 0,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === "queued" || status === "building" ? 1_500 : false;
    },
  });
}

export function useGalleryVersionURLs(assetId: string, versionId: string, enabled = true) {
  return useQuery({
    queryKey: ["gallery", "version-urls", assetId, versionId],
    queryFn: () => getVersionURLs(assetId, versionId),
    enabled: enabled && assetId.length > 0 && versionId.length > 0,
    staleTime: 10 * 60_000,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: false,
  });
}

export function useGalleryComments(assetId: string, enabled = true) {
  return useQuery({
    queryKey: galleryKeys.comments(assetId),
    queryFn: () => getGalleryComments(assetId),
    enabled: enabled && assetId.length > 0,
  });
}

export function useGalleryShares(albumId: string, enabled = true) {
  return useQuery({
    queryKey: ["gallery", "shares", albumId],
    queryFn: () => getGalleryShares(albumId),
    enabled: enabled && albumId.length > 0,
  });
}

export function useGalleryTagCatalog(enabled = true) {
  return useQuery({
    queryKey: ["gallery", "tag-catalog"],
    queryFn: getGalleryTagCatalog,
    enabled,
    staleTime: 60_000,
  });
}

export function useGalleryAlbumTags(albumId: string, enabled = true) {
  return useQuery({
    queryKey: ["gallery", "album-tags", albumId],
    queryFn: () => getGalleryAlbumTags(albumId),
    enabled: enabled && albumId.length > 0,
  });
}

export function useGalleryTrash(orderId: string, enabled = true, source: GallerySource = "order") {
  return useQuery({
    queryKey: galleryKeys.trash(orderId),
    queryFn: () => getGalleryTrash(orderId, source),
    enabled: enabled && orderId.length > 0,
  });
}

export function useGallerySelection(albumId: string, enabled = true) {
  return useQuery({
    queryKey: galleryKeys.selection(albumId),
    queryFn: () => getGallerySelection(albumId),
    enabled: enabled && albumId.length > 0,
  });
}

export function useRequestGalleryArchive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: requestArchive,
    onSuccess: (job) => queryClient.setQueryData(galleryKeys.archive(job.id), job),
  });
}

export function useGalleryMutations() {
  const queryClient = useQueryClient();
  // Số ảnh trên cây và ba bộ lọc nhanh đều nằm dưới `["gallery", "order"]`.
  // Việc nào đổi tấm nào nằm ở album nào, hay đổi cờ của tấm, phải làm mới cả
  // hai — không thì cây ghi "22 ảnh" trong khi lưới vừa bớt một tấm.
  const invalidateCounts = () => {
    void queryClient.invalidateQueries({ queryKey: ["gallery", "order"] });
  };
  const invalidateAlbum = (albumId: string) => {
    void queryClient.invalidateQueries({ queryKey: ["gallery", "assets", albumId] });
    invalidateCounts();
  };
  // Tấm vào / ra thùng rác, hay đổi album: cây, lưới (mọi album — tấm hiện ở cả
  // album cha khi mở "Gồm album con") và thùng rác đều phải nói cùng một chuyện.
  const invalidateTreeAssetsTrash = () => {
    invalidateCounts();
    void queryClient.invalidateQueries({ queryKey: ["gallery", "assets"] });
    void queryClient.invalidateQueries({ queryKey: ["gallery", "trash"] });
  };
  return {
    createAlbum: useMutation({
      mutationFn: ({ parentId, body }: { parentId: string; body: GalleryAlbumInput }) =>
        createGalleryAlbum(parentId, body),
      // Returned (not voided) so mutateAsync resolves only once the tree carries
      // the new folder: the workspace opens it right after, and a tree without
      // it falls back to the root — uploads then land in the root (04/10/2026).
      onSuccess: (album) =>
        queryClient.invalidateQueries({ queryKey: galleryKeys.root(album.orderId) }),
    }),
    updateAlbum: useMutation({
      mutationFn: ({ albumId, body }: { albumId: string; body: GalleryAlbumPatch }) =>
        updateGalleryAlbum(albumId, body),
      onSuccess: (album) =>
        void queryClient.invalidateQueries({ queryKey: galleryKeys.root(album.orderId) }),
    }),
    returnAlbumToPhoto: useMutation({
      mutationFn: ({ albumId, body }: { albumId: string; body: GalleryAlbumReturnInput }) =>
        returnGalleryAlbumToPhoto(albumId, body),
      // CỐ Ý không nạp lại gì. Đo 26/09/2026: lượt trả chỉ ghi một dòng nhật ký
      // (`album_returned_to_photo`) và có thể một phiếu lỗi — nó KHÔNG sửa bảng
      // album, mà cây album cũng không mang trường nào nói về lượt trả. Nạp lại
      // cây là gọi mạng để nhận về đúng thứ vừa có. Ngày nào cây mang dấu "đã
      // trả" thì thêm `galleryKeys.root(ret.orderId)` vào đây.
    }),
    deleteAlbum: useMutation({
      mutationFn: ({
        albumId,
        itemDisposition,
      }: {
        albumId: string;
        itemDisposition: "move_to_parent" | "unlink";
      }) => deleteGalleryAlbum(albumId, itemDisposition),
      onSuccess: invalidateTreeAssetsTrash,
    }),
    mergeAlbum: useMutation({
      mutationFn: ({ albumId, expectedRevision }: { albumId: string; expectedRevision: number }) =>
        mergeGalleryAlbum(albumId, expectedRevision),
      onSuccess: invalidateTreeAssetsTrash,
    }),
    // Chuyển tấm sang thư mục khác — một tấm (panel) hay cả lô (Chọn nhiều) đều đi
    // cửa này: một giao dịch, một luật quyền ở máy chủ.
    moveItems: useMutation({
      mutationFn: ({
        targetAlbumId,
        fromAlbumId,
        assetIds,
      }: {
        targetAlbumId: string;
        fromAlbumId: string;
        assetIds: string[];
      }) => moveGalleryAlbumItems(targetAlbumId, fromAlbumId, assetIds),
      onSuccess: invalidateTreeAssetsTrash,
    }),
    restoreTrash: useMutation({
      mutationFn: ({ orderId, assetIds, source }: { orderId: string; assetIds: string[]; source?: GallerySource }) =>
        restoreGalleryTrash(orderId, assetIds, source),
      onSuccess: invalidateTreeAssetsTrash,
    }),
    // Xoá hẳn chỉ đổi thùng rác: tấm đã rời mọi album từ lúc bị xoá.
    purgeTrash: useMutation({
      mutationFn: ({ orderId, assetIds, source }: { orderId: string; assetIds: string[]; source?: GallerySource }) =>
        purgeGalleryTrash(orderId, assetIds, source),
      onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["gallery", "trash"] }),
    }),
    setAlbumTag: useMutation({
      mutationFn: ({
        albumId,
        tagId,
        included,
        reason,
      }: {
        albumId: string;
        tagId: string;
        included: boolean;
        reason?: string;
      }) => setGalleryAlbumTag(albumId, tagId, included, reason),
      // Cây album mang chấm nhãn của từng nhánh — gắn / gỡ nhãn phải làm mới
      // cả cây, không thì chấm trên cây nói một đằng, hộp nhãn nói một nẻo.
      onSuccess: (_data, input) => {
        void queryClient.invalidateQueries({ queryKey: ["gallery", "album-tags", input.albumId] });
        invalidateCounts();
      },
    }),
    removeItem: useMutation({
      mutationFn: ({ albumId, assetId }: { albumId: string; assetId: string }) =>
        removeGalleryAlbumItem(albumId, assetId),
      // Gỡ tấm cuối cùng của nó là tấm vào thùng rác.
      onSuccess: (_data, input) => {
        invalidateAlbum(input.albumId);
        void queryClient.invalidateQueries({ queryKey: ["gallery", "trash"] });
      },
    }),
    // Xoá nhiều tấm: lưới của mọi album (tấm hiện ở album cha khi "Gồm album
    // con"), số trên cây và thùng rác cùng đổi.
    unlinkItems: useMutation({
      mutationFn: ({ albumId, body }: { albumId: string; body: { assetIds: string[] } | { all: true } }) =>
        unlinkGalleryAlbumItems(albumId, body),
      onSuccess: invalidateTreeAssetsTrash,
    }),
    updateItem: useMutation({
      mutationFn: ({
        albumId,
        assetId,
        body,
      }: {
        albumId: string;
        assetId: string;
        body: GalleryAlbumItemInput;
      }) => updateGalleryAlbumItem(albumId, assetId, body),
      onSuccess: (_data, input) => invalidateAlbum(input.albumId),
    }),
    marker: useMutation({
      mutationFn: ({ assetId, body }: { assetId: string; body: GalleryMarkerInput }) =>
        updateGalleryMarkers(assetId, body),
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ["gallery", "assets"] });
        invalidateCounts();
      },
    }),
    selection: useMutation({
      mutationFn: ({
        albumId,
        assetId,
        selected,
      }: {
        albumId: string;
        assetId: string;
        selected: boolean;
      }) => updateGallerySelection(albumId, assetId, selected),
      onSuccess: (state) => {
        queryClient.setQueryData(galleryKeys.selection(state.albumId), state);
        invalidateCounts();
      },
    }),
    finalSelection: useMutation({
      mutationFn: ({
        albumId,
        assetId,
        selected,
      }: {
        albumId: string;
        assetId: string;
        selected: boolean;
      }) => updateGalleryFinalSelection(albumId, assetId, selected),
      onSuccess: (state) => {
        queryClient.setQueryData(galleryKeys.selection(state.albumId), state);
        invalidateCounts();
      },
    }),
    commitSelection: useMutation({
      mutationFn: commitGallerySelection,
      onSuccess: async (state) => {
        queryClient.setQueryData(galleryKeys.selection(state.albumId), state);
        await queryClient.invalidateQueries({ queryKey: ["gallery", "order"] });
      },
    }),
    reopenSelection: useMutation({
      mutationFn: ({ albumId, reason }: { albumId: string; reason: string }) =>
        reopenGallerySelection(albumId, reason),
      onSuccess: async (state) => {
        queryClient.setQueryData(galleryKeys.selection(state.albumId), state);
        await queryClient.invalidateQueries({ queryKey: ["gallery", "order"] });
      },
    }),
    dropExtraSelections: useMutation({
      mutationFn: ({ albumId, assetIds }: { albumId: string; assetIds: string[] }) =>
        dropGalleryExtraSelections(albumId, assetIds),
      onSuccess: async (state) => {
        queryClient.setQueryData(galleryKeys.selection(state.albumId), state);
        // Tấm bỏ rời "3. Khách chốt" và "5. Ảnh hoàn thiện" — làm tươi cây, ảnh, đếm.
        await queryClient.invalidateQueries({ queryKey: ["gallery", "order"] });
        await queryClient.invalidateQueries({ queryKey: ["gallery", "assets"] });
        invalidateCounts();
      },
    }),
    comment: useMutation({
      mutationFn: ({ assetId, body }: { assetId: string; body: GalleryCommentInput }) =>
        createGalleryComment(assetId, body),
      onSuccess: (_data, input) =>
        void queryClient.invalidateQueries({ queryKey: galleryKeys.comments(input.assetId) }),
    }),
    commentState: useMutation({
      mutationFn: ({ commentId, action }: { commentId: string; action: "resolve" | "reopen" }) =>
        updateGalleryComment(commentId, action),
    }),
    retry: useMutation({
      mutationFn: ({ assetId, versionId }: { assetId: string; versionId: string }) =>
        retryGalleryVersion(assetId, versionId),
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ["gallery", "assets"] });
        invalidateCounts();
      },
    }),
  };
}

function publicScopeKey(scope: PublicGalleryScope) {
  return scope.kind === "share" ? ["share", scope.token] : ["portal", scope.orderId];
}

function publicGalleryPath(scope: PublicGalleryScope, suffix = "") {
  if (scope.kind === "share") {
    return `${galleryPublicAPIBase}/gallery-shares/${encodeURIComponent(scope.token)}${suffix}`;
  }
  return `${galleryPublicAPIBase}/public/customer-orders/${encodeURIComponent(scope.orderId)}/gallery${suffix}`;
}

export class PublicGalleryError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "PublicGalleryError";
  }
}

async function publicGalleryRequest<T>(
  scope: PublicGalleryScope,
  suffix = "",
  init: RequestInit = {},
): Promise<T> {
  const response = await fetch(publicGalleryPath(scope, suffix), {
    ...init,
    credentials: scope.kind === "portal" ? "include" : "omit",
  });
  if (!response.ok) {
    // Câu riêng chỉ cho link hết hạn / bị thu hồi và phiên đăng nhập hết hạn; lý do
    // khác của máy chủ (đã chốt, chọn ngoài phần mình, bấm quá nhanh…) phải tới được
    // khách qua bộ dịch — trước 13/10/2026 mọi lý do thành "link không còn khả dụng"
    // nên khách tưởng link chết (Luật 6 AGENTS.md).
    const fallback =
      scope.kind === "share" ? "Liên kết album không còn khả dụng." : "Không tải được album của bạn.";
    const body: unknown = await response.json().catch(() => null);
    const message =
      scope.kind === "share" && (response.status === 404 || response.status === 410)
        ? "Liên kết album không còn khả dụng."
        : scope.kind === "portal" && response.status === 401
          ? "Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại bằng số điện thoại."
          : serverErrorMessage(body, fallback);
    throw new PublicGalleryError(message, response.status);
  }
  return (await response.json()) as T;
}

export function openSharedGallery(token: string): Promise<PublicGallery> {
  return publicGalleryRequest({ kind: "share", token });
}

export function openPortalGallery(orderId: string): Promise<PublicGallery> {
  return publicGalleryRequest({ kind: "portal", orderId });
}

export function listPublicGalleryAssets(
  scope: PublicGalleryScope,
  cursor?: string,
): Promise<PublicGalleryAssetPage> {
  const query = new URLSearchParams({ limit: "100" });
  if (cursor) query.set("cursor", cursor);
  return publicGalleryRequest(scope, `/assets?${query}`);
}

export function getPublicGalleryVersionURLs(
  scope: PublicGalleryScope,
  assetId: string,
  versionId: string,
): Promise<GallerySignedVersionURLs> {
  return publicGalleryRequest(
    scope,
    `/assets/${encodeURIComponent(assetId)}/versions/${encodeURIComponent(versionId)}/urls`,
  );
}

export function getPublicGalleryComments(
  scope: PublicGalleryScope,
  assetId: string,
): Promise<PublicGalleryComment[]> {
  return publicGalleryRequest(scope, `/assets/${encodeURIComponent(assetId)}/comments`);
}

export function createPublicGalleryComment(
  scope: PublicGalleryScope,
  assetId: string,
  body: GalleryCommentInput,
): Promise<PublicGalleryComment> {
  return publicGalleryRequest(scope, `/assets/${encodeURIComponent(assetId)}/comments`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function getPublicGallerySelection(
  scope: PublicGalleryScope,
): Promise<PublicGallerySelectionState> {
  return publicGalleryRequest(scope, "/selections");
}

export function updatePublicGallerySelection(
  scope: PublicGalleryScope,
  assetId: string,
  selected: boolean,
): Promise<PublicGallerySelectionState> {
  return publicGalleryRequest(scope, `/selections/${encodeURIComponent(assetId)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ selected }),
  });
}

export function updatePublicGalleryFinalSelection(
  scope: PublicGalleryScope,
  assetId: string,
  selected: boolean,
): Promise<PublicGallerySelectionState> {
  return publicGalleryRequest(scope, `/final-selections/${encodeURIComponent(assetId)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ selected }),
  });
}

export function commitPublicGallerySelection(
  scope: PublicGalleryScope,
): Promise<PublicGallerySelectionState> {
  return publicGalleryRequest(scope, "/selections/commit", { method: "POST" });
}

/**
 * Queue a ZIP of the shared album. Pass assetIds to narrow it to those photos (a customer
 * downloading only what they picked); an empty list archives everything the link may download.
 */
export function requestPublicGalleryArchive(
  scope: PublicGalleryScope,
  assetIds: string[] = [],
): Promise<PublicGalleryArchiveJob> {
  return publicGalleryRequest(scope, "/archives", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      includeDescendants: true,
      ...(assetIds.length > 0 ? { assetIds } : {}),
    }),
  });
}

export function getPublicGalleryArchive(
  scope: PublicGalleryScope,
  archiveId: string,
): Promise<PublicGalleryArchiveJob> {
  return publicGalleryRequest(scope, `/archives/${encodeURIComponent(archiveId)}`);
}

export function getPublicGalleryArchiveDownload(
  scope: PublicGalleryScope,
  archiveId: string,
): Promise<GallerySignedDownload> {
  return publicGalleryRequest(scope, `/archives/${encodeURIComponent(archiveId)}/download`);
}

export function usePublicGallery(scope: PublicGalleryScope) {
  return useQuery({
    queryKey: ["gallery-public", ...publicScopeKey(scope), "root"],
    queryFn: () =>
      scope.kind === "share" ? openSharedGallery(scope.token) : openPortalGallery(scope.orderId),
    retry: false,
  });
}

export function useInfinitePublicGalleryAssets(scope: PublicGalleryScope) {
  return useInfiniteQuery({
    queryKey: ["gallery-public", ...publicScopeKey(scope), "assets"],
    queryFn: ({ pageParam }) => listPublicGalleryAssets(scope, pageParam || undefined),
    initialPageParam: "",
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    staleTime: 10 * 60_000,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: false,
  });
}

export function usePublicGallerySelection(scope: PublicGalleryScope, enabled: boolean) {
  return useQuery({
    queryKey: ["gallery-public", ...publicScopeKey(scope), "selection"],
    queryFn: () => getPublicGallerySelection(scope),
    enabled,
  });
}

export function usePublicGalleryVersionURLs(
  scope: PublicGalleryScope,
  assetId: string,
  versionId: string,
  enabled = true,
) {
  return useQuery({
    queryKey: ["gallery-public", ...publicScopeKey(scope), "urls", assetId, versionId],
    queryFn: () => getPublicGalleryVersionURLs(scope, assetId, versionId),
    enabled: enabled && !!assetId && !!versionId,
    staleTime: 10 * 60_000,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function usePublicGalleryComments(
  scope: PublicGalleryScope,
  assetId: string,
  enabled = true,
) {
  return useQuery({
    queryKey: ["gallery-public", ...publicScopeKey(scope), "comments", assetId],
    queryFn: () => getPublicGalleryComments(scope, assetId),
    enabled: enabled && !!assetId,
  });
}

export function usePublicGalleryArchive(
  scope: PublicGalleryScope,
  archiveId: string,
  enabled = true,
) {
  return useQuery({
    queryKey: ["gallery-public", ...publicScopeKey(scope), "archive", archiveId],
    queryFn: () => getPublicGalleryArchive(scope, archiveId),
    enabled: enabled && !!archiveId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === "queued" || status === "building" ? 1_500 : false;
    },
  });
}

export function usePublicGalleryMutations(scope: PublicGalleryScope) {
  const queryClient = useQueryClient();
  const selectionKey = ["gallery-public", ...publicScopeKey(scope), "selection"];
  return {
    select: useMutation({
      mutationFn: ({ assetId, selected }: { assetId: string; selected: boolean }) =>
        updatePublicGallerySelection(scope, assetId, selected),
      onSuccess: (state) => queryClient.setQueryData(selectionKey, state),
    }),
    selectFinal: useMutation({
      mutationFn: ({ assetId, selected }: { assetId: string; selected: boolean }) =>
        updatePublicGalleryFinalSelection(scope, assetId, selected),
      onSuccess: (state) => queryClient.setQueryData(selectionKey, state),
    }),
    commit: useMutation({
      mutationFn: () => commitPublicGallerySelection(scope),
      onSuccess: (state) => queryClient.setQueryData(selectionKey, state),
    }),
    comment: useMutation({
      mutationFn: ({ assetId, body }: { assetId: string; body: GalleryCommentInput }) =>
        createPublicGalleryComment(scope, assetId, body),
      onSuccess: (_comment, input) =>
        void queryClient.invalidateQueries({
          queryKey: ["gallery-public", ...publicScopeKey(scope), "comments", input.assetId],
        }),
    }),
    archive: useMutation({
      mutationFn: (assetIds: string[]) => requestPublicGalleryArchive(scope, assetIds),
    }),
  };
}
